import type { AgentMessageImage, AgentMessageRow } from "@/lib/agent-api"
import { videoFirstFrameSrc } from "@/lib/video-timeline"

export type AgentMessageChipPreview = { kind: "image"; src: string } | { kind: "video"; src: string }

function isVideoAttachment(image: AgentMessageImage): boolean {
  return image.content_type?.startsWith("video/") === true
}

/** Чем рисовать чип 64px. Локальный preview_url важнее серверного файла. */
export function agentMessageChipPreview(image: AgentMessageImage): AgentMessageChipPreview | null {
  const preview = image.preview_url?.trim() || null
  if (preview) {
    if (isVideoAttachment(image) && preview.startsWith("blob:")) return { kind: "video", src: preview }
    return { kind: "image", src: preview }
  }

  if (isVideoAttachment(image)) {
    const src = videoFirstFrameSrc(image.stream_url || image.download_url)
    return src ? { kind: "video", src } : null
  }

  const thumb = image.thumb_url || image.download_url
  return thumb ? { kind: "image", src: thumb } : null
}

/** Переносит локальные preview_url на серверные вложения по индексу. */
export function withLocalPreviewUrls(
  images: AgentMessageImage[] | null | undefined,
  local: readonly Pick<AgentMessageImage, "preview_url">[] | null | undefined,
): AgentMessageImage[] | undefined {
  if (!images) return undefined
  if (!local?.length) return images

  let changed = false
  const next = images.map((image, index) => {
    const preview = local[index]?.preview_url
    if (!preview || image.preview_url === preview) return image
    changed = true
    return { ...image, preview_url: preview }
  })
  return changed ? next : images
}

export function revokeBlobPreviewUrls(rows: readonly Pick<AgentMessageRow, "images">[]): void {
  for (const row of rows) {
    for (const image of row.images ?? []) {
      const url = image.preview_url
      if (url?.startsWith("blob:")) URL.revokeObjectURL(url)
    }
  }
}

export function oldestPositiveId(rows: readonly AgentMessageRow[]): number | null {
  let oldest: number | null = null
  for (const row of rows) {
    if (row.id <= 0) continue
    if (oldest == null || row.id < oldest) oldest = row.id
  }
  return oldest
}

function sameText(left: string | null | undefined, right: string | null | undefined): boolean {
  return (left ?? "") === (right ?? "")
}

function withClientPreview(row: AgentMessageRow, source: AgentMessageRow): AgentMessageRow {
  const images = withLocalPreviewUrls(row.images, source.images)
  const clientKey = source.clientKey ?? row.clientKey
  if (images && images !== row.images) return { ...row, clientKey, images }
  if (clientKey && clientKey !== row.clientKey) return { ...row, clientKey }
  return row
}

/**
 * Хвост истории заменяет последние реплики. Если выше уже подгружены страницы,
 * они остаются: у них id меньше первой реплики этой страницы.
 * Локальный preview_url переживает подмену оптимистичной реплики и следующий опрос.
 */
export function mergeHistoryMessages(
  prev: AgentMessageRow[],
  next: AgentMessageRow[],
  hasOlder: boolean,
): AgentMessageRow[] {
  const oldestIncoming = oldestPositiveId(next)
  const prefix = hasOlder && oldestIncoming != null ? prev.filter((row) => row.id > 0 && row.id < oldestIncoming) : []
  const optimistic = prev.filter((row) => row.id < 0)
  const knownIds = new Set(prev.map((row) => row.id))
  const consumedKeys = new Set<string>()
  const tail = next.map((row) => {
    const prior = prev.find((item) => item.id === row.id && item.clientKey)
    if (prior?.clientKey) return withClientPreview(row, prior)
    if (row.role === "user" && !knownIds.has(row.id)) {
      const match = optimistic.find(
        (item) =>
          item.role === "user" &&
          item.clientKey != null &&
          !consumedKeys.has(item.clientKey) &&
          sameText(item.content, row.content),
      )
      if (match?.clientKey) {
        consumedKeys.add(match.clientKey)
        return withClientPreview(row, match)
      }
    }
    return row
  })
  const keptOptimistic = optimistic.filter((item) => {
    if (item.clientKey && consumedKeys.has(item.clientKey)) return false
    if (item.role !== "user") return true
    return !tail.some((row) => row.role === "user" && row.id > 0 && sameText(row.content, item.content))
  })
  const mergedTail = keptOptimistic.length > 0 ? [...tail, ...keptOptimistic] : tail
  return prefix.length > 0 ? [...prefix, ...mergedTail] : mergedTail
}
